import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import LoadingSpinner from './LoadingSpinner'

export type ConfirmDialogTone = 'danger' | 'neutral'

type Props = {
  open: boolean
  title: string
  description: React.ReactNode
  confirmLabel: string
  cancelLabel?: string
  tone?: ConfirmDialogTone
  confirmBusy?: boolean
  /** Shown on the confirm button while `confirmBusy` is true. Defaults to "Importing…". */
  confirmBusyLabel?: string
  /** Stack above nested modals (e.g. AlxModalShell at 10010). */
  zIndex?: number
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'neutral',
  confirmBusy,
  confirmBusyLabel = 'Importing…',
  zIndex = 10000,
  onConfirm,
  onCancel
}: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmBusy) onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, confirmBusy, onCancel])

  if (!open) return null

  const content = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 px-4"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            onClick={onCancel}
            disabled={confirmBusy}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-3 text-sm text-gray-600">{description}</div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={confirmBusy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={clsx(
              'inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              tone === 'danger'
                ? 'bg-error-600 hover:bg-error-700 focus:ring-error-500'
                : 'btn-primary focus:ring-primary-500'
            )}
            onClick={() => void onConfirm()}
            disabled={confirmBusy}
          >
            {confirmBusy ? (
              <>
                <LoadingSpinner size="sm" className="mr-2 border-white border-r-transparent" />
                {confirmBusyLabel}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
